import { c as _c } from "react/compiler-runtime";
import SceneLogo from "@assets/logo/SCENE_logo.svg?react";
import SphereXgLogo from "@assets/logo/sphere-xg-logo.svg?react";
import { ManageSeatsButton } from "@components/table/members/assign-seats/manage-seats-button";
import { shouldOfferToAssignSeats } from "@components/table/members/assign-seats/assign-seats-utils";
import { useMemberSeats } from "@components/table/members/assign-seats/use-member-seats";
import { Tooltip } from "@flat-ui";
import { Box, Stack } from "@mui-wrapper";
import { getHeldSeatDescription, getHeldSeatList, getHeldSeatsDescription } from "@utils/member-seats-utils";

/** The size of the seat logos, which matches the other icons of the table */
const LOGO_SIZE = "24px";

/** The logo of each seat a member can hold, with the test ID to tell them apart */
const HELD_SEAT_LOGOS = {
  xgSeat: {
    Logo: SphereXgLogo,
    testId: "xg-seat-icon"
  },
  xgActiveUserSeat: {
    Logo: SphereXgLogo,
    testId: "xg-active-user-seat-icon"
  },
  sceneSeat: {
    Logo: SceneLogo,
    testId: "scene-access-icon"
  }
};
/** Shows the logo of a seat a member holds */
function HeldSeatLogo(t0) {
  const $ = _c(13);
  const {
    seat,
    isOverlapping,
    zIndex
  } = t0;
  const {
    Logo,
    testId
  } = HELD_SEAT_LOGOS[seat];
  const t1 = isOverlapping ? -1 : 0;
  let t2;
  if ($[0] !== t1 || $[1] !== zIndex) {
    t2 = {
      borderRadius: "50%",
      backgroundColor: "background.paper",
      ml: t1,
      position: "relative",
      zIndex
    };
    $[0] = t1;
    $[1] = zIndex;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== seat) {
    t3 = getHeldSeatDescription(seat);
    $[3] = seat;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      display: "block"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== Logo || $[7] !== t3 || $[8] !== testId) {
    t5 = <Logo height={LOGO_SIZE} width={LOGO_SIZE} data-testid={testId} role="img" aria-label={t3} style={t4} />;
    $[6] = Logo;
    $[7] = t3;
    $[8] = testId;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t5) {
    t6 = <Box sx={t2}>{t5}</Box>;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
/**
 * Shows a logo per seat a member holds, partly covering each other like the avatars of a group, so
 * that the cell stays narrow. The first logo, Sphere XG, is drawn on top of the ones after it.
 */
function HeldSeatLogos(t0) {
  const $ = _c(20);
  const {
    heldSeats
  } = t0;
  let t1;
  if ($[0] !== heldSeats) {
    t1 = getHeldSeatsDescription(heldSeats);
    $[0] = heldSeats;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const description = t1;
  if (!description) {
    return null;
  }
  let T0;
  let T1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] !== description || $[3] !== heldSeats) {
    const seats = getHeldSeatList(heldSeats);
    T1 = Tooltip;
    t5 = description;
    T0 = Stack;
    t2 = "row";
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        alignItems: "center"
      };
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t4 = seats.map((seat, index) => <HeldSeatLogo key={seat} seat={seat} isOverlapping={index > 0} zIndex={seats.length - index} />);
    $[2] = description;
    $[3] = heldSeats;
    $[4] = T0;
    $[5] = T1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    T0 = $[4];
    T1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  let t6;
  if ($[11] !== T0 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t6 = <T0 direction={t2} sx={t3}>{t4}</T0>;
    $[11] = T0;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== T1 || $[17] !== t5 || $[18] !== t6) {
    t7 = <T1 title={t5}>{t6}</T1>;
    $[16] = T1;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}

/**
 * Shows the seats of the workspace subscription that a member holds, see getHeldSeats.
 *
 * Every seat is marked with the logo of its product rather than a text chip, so that the column
 * stays narrow in every language. The cell of a member without a seat stays empty instead of
 * stating that they have none, since that is the default state and marking every one of them would
 * add noise to the table. Users that are allowed to hand out a seat the member does not hold yet
 * are offered that action next to the logos instead, but only while the row is hovered.
 */
export function SeatsColumn(t0) {
  const $ = _c(9);
  const {
    member
  } = t0;
  const {
    heldSeats,
    seatOptions
  } = useMemberSeats(member);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignItems: "center",
      gap: 1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== heldSeats) {
    t2 = <HeldSeatLogos heldSeats={heldSeats} />;
    $[1] = heldSeats;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== member || $[4] !== seatOptions) {
    t3 = shouldOfferToAssignSeats(seatOptions) && <ManageSeatsButton member={member} />;
    $[3] = member;
    $[4] = seatOptions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <Stack direction="row" sx={t1}>{t2}{t3}</Stack>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}